import { type DiscountedPrice, type ProductVariant } from '@commercetools/platform-sdk'
import { type TFunction } from 'i18next'
import { getAttributeValue } from '#app/utils/attribute-util.ts'
import { moneyFormatWithTypedMoney } from '#app/utils/money'
import {type PlentyVariant} from '#types/product'

export const getVariantSize = (variant: ProductVariant) => {
	const size = getAttributeValue(variant, 'size')
	const length = getAttributeValue(variant, 'length')

	return length ? `${size} / ${length.replace('"', '')}` : `${size}`
}

export const getGenderBasedOnCategorySlug = (categorySlug: string) => {
	if (categorySlug.includes('women')) {
		return 'women'
	} else if (categorySlug.includes('men')) {
		return 'men'
	} else {
		return 'kids'
	}
}


export const getBadgeForSelectItem = (
	discounted: DiscountedPrice | undefined,
	outOfStock: boolean,
	fewStock: number,
	translationInstance: TFunction<'product_details_page', undefined>,
) => {
	if (discounted)
		return {
			type: 'sale',
			text: translationInstance('now_for_sale', {
				saleValue: moneyFormatWithTypedMoney(discounted.value),
			}),
		}
	else if (fewStock <= 2 && fewStock > 0)
		return {
			type: 'fewStock',
			text: translationInstance('only_left', { count: fewStock }),
		}
	return undefined
}

export const getSizeOptions = (
	activeStyle: any,
	loaderData: any,
	t: TFunction<'product_details_page', undefined>,
	possibleSizes?: string[],
) => {
	const sizeSet = possibleSizes || []

	return sizeSet.map((rawSize: string) => {
		// Special case for "ONE SIZE" - treat it as a single value without splitting
		if (rawSize.toUpperCase() === "ONE SIZE") {
			const size = rawSize
			// Find variant with matching size if it exists
			const sizeVariant: PlentyVariant = activeStyle?.variants.find((variant: PlentyVariant) =>
				variant.attributes?.find(attr => attr.name === 'size')?.value === rawSize,
			)
			const sizeAvailability = activeStyle.generalAvailability.bySize[size]

			if (!sizeVariant) {
				return {
					label: size,
					value: size,
					disabled: true,
					availableQuantity: 0,
					variant: undefined,
					badgeProps: {
						type: 'soldOut',
						text: t('out_of_stock')
					}
				}
			}

			if (sizeVariant.availability?.length === 0 || sizeVariant.availability?.every(it => !it.isOnStock)) {
				// if the variant was not found, it means it is not available for the store if there is one filtered
				// we can still check for the store availability by size
				// if it is available in any store, we can show the badge
				if (sizeAvailability?.totalQuantityAcrossStores > 0) {
					// Find stores with stock
					const availableStores = sizeAvailability.storeAvailability.filter((store: {
						storeKey: string
						storeName: string | undefined
						quantity: number
					}) => store.quantity > 0)
					
					const storeWithMoreStock = availableStores.reduce(
						(prev: { quantity: number }, current: { quantity: number }) =>
							prev.quantity > current.quantity ? prev : current,
					)
					
					return {
						label: size,
						value: sizeAvailability?.sku,
						disabled: false,
						availableQuantity: 0,
						variant: sizeVariant,
						badgeProps: {
							type: 'availableInStore',
							text: t('available_in_stores', {
								count: availableStores.length,
								storeName: storeWithMoreStock.storeName,
								quantity: storeWithMoreStock.quantity
							})
						}
					}
				}

				return {
					label: size,
					value: size,
					disabled: true,
					availableQuantity: 0,
					variant: sizeVariant,
					badgeProps: {
						type: 'soldOut',
						text: t('out_of_stock')
					}
				}
			}

			const isOutOfStock = !sizeVariant.availability?.find(it => it.isOnStock)
			const isPriceMissingChannel = !sizeVariant.prices?.some(item => item.channel?.id)
			const availableQuantity = sizeVariant.availability?.reduce((total, availability) => {
				const quantity = (availability.availableQuantity as number) || 0
				return total + quantity
			}, 0) || 0

			const selectedAvailability = sizeVariant.availability?.find(
				it =>
					(it.availableQuantity as number) > 0 &&
					loaderData?.storeParams.includes(it.storeKey as string),
			)

			const discountedPrice = loaderData?.storeParams.length
				? selectedAvailability?.channelKey === sizeVariant.lowestPrice.channel?.id
					? sizeVariant.lowestPrice.discounted
					: undefined
				: sizeVariant.lowestPrice.discounted

			let badge = getBadgeForSelectItem(
				discountedPrice,
				isOutOfStock,
				availableQuantity,
				t,
			)

			return {
				label: size,
				value: sizeVariant.sku!,
				disabled: isOutOfStock || isPriceMissingChannel,
				availableQuantity,
				variant: sizeVariant,
				badgeProps: badge
			}
		}

		// Regular case for other sizes
		//Figure it out if size has length on the string and change it accordingly, e.g: 33 "32 -> 33 / 32
		const terms = rawSize.split(' ')
		const possibleLength = terms[1]?.replace('"', '')
		const size = `${terms[0]}${terms[1] ? ` / ${possibleLength}` : ''}`
		// Find variant with matching size if it exists, take into account the length if it exists
		const sizeVariant: PlentyVariant = activeStyle?.variants.find((variant: PlentyVariant) =>
			variant.attributes?.find(attr => attr.name === 'size')?.value === terms[0] &&
			(!possibleLength || variant.attributes?.find(attr => attr.name === 'length')?.value === terms[1]),
		)
		const sizeAvailability = activeStyle.generalAvailability.bySize[size]

		if (!sizeVariant) {
			return {
				label: size,
				value: size,
				disabled: true,
				availableQuantity: 0,
				variant: undefined,
				badgeProps: {
					type: 'soldOut',
					text: t('out_of_stock')
				}
			}
		}

		if (sizeVariant.availability?.length === 0 || sizeVariant.availability?.every(it => !it.isOnStock)) {
			// if the variant was not found, it means it is not available for the store if there is one filtered
			// we can still check for the store availability by size
			// if it is available in any store, we can show the badge
			if (sizeAvailability?.totalQuantityAcrossStores > 0) {
				// Find stores with stock
				const availableStores = sizeAvailability.storeAvailability.filter((store: {
					storeKey: string
					storeName: string | undefined
					quantity: number
				}) => store.quantity > 0)
				
				const storeWithMoreStock = availableStores.reduce(
					(prev: { quantity: number }, current: { quantity: number }) =>
						prev.quantity > current.quantity ? prev : current,
				)
				
				return {
					label: size,
					value: sizeAvailability?.sku,
					disabled: false,
					availableQuantity: 0,
					variant: sizeVariant,
					badgeProps: {
						type: 'availableInStore',
						text: t('available_in_stores', {
							count: availableStores.length,
							storeName: storeWithMoreStock.storeName,
							quantity: storeWithMoreStock.quantity
						})
					}
				}
			}

			return {
				label: size,
				value: size,
				disabled: true,
				availableQuantity: 0,
				variant: sizeVariant,
				badgeProps: {
					type: 'soldOut',
					text: t('out_of_stock')
				}
			}
		}

		const productSize = sizeVariant.attributes?.find(item => item.name === 'size')?.value
		const length = sizeVariant.attributes?.find(item => item.name === 'length')?.value
		const isOutOfStock = !sizeVariant.availability?.find(it => it.isOnStock)
		const isPriceMissingChannel = !sizeVariant.prices?.some(item => item.channel?.id)
		const availableQuantity = sizeVariant.availability?.reduce((total, availability) => {
			const quantity = (availability.availableQuantity as number) || 0
			return total + quantity
		}, 0) || 0

		const selectedAvailability = sizeVariant.availability?.find(
			it =>
				(it.availableQuantity as number) > 0 &&
				loaderData?.storeParams.includes(it.storeKey as string),
		)

		const discountedPrice = loaderData?.storeParams.length
			? selectedAvailability?.channelKey === sizeVariant.lowestPrice.channel?.id
				? sizeVariant.lowestPrice.discounted
				: undefined
			: sizeVariant.lowestPrice.discounted

		let badge = getBadgeForSelectItem(
			discountedPrice,
			isOutOfStock,
			availableQuantity,
			t,
		)

		return {
			label: length ? `${productSize} / ${length.replace('"', '')}` : productSize,
			value: sizeVariant.sku!,
			disabled: isOutOfStock || isPriceMissingChannel,
			availableQuantity,
			variant: sizeVariant,
			badgeProps: badge
		}
	})
}